import { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useLanguage } from '@/lib/i18n';
import { useSalonProfile } from '@/contexts/SalonProfileContext';
import { isUseMode, getAvailable, getMinThreshold, getCostPerUse, getDerivedUnitCount, isLowStock, isOutOfStock } from '@/lib/inventoryUtils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { Search, Plus, Minus, Trash2, CreditCard, Banknote, Smartphone, ArrowLeftRight, CheckCircle2, FileText, MessageCircle, RotateCcw, User, Clock, Award, Package, X, AlertTriangle } from 'lucide-react';
import jsPDF from 'jspdf';
import confetti from 'canvas-confetti';

function apiProductToCamel(p) {
  if (!p) return p;
  return {
    id: p.id,
    name: p.name,
    category: p.category,
    unit: p.unit,
    stock: p.stock,
    minStock: p.min_stock,
    costPrice: p.cost_price,
    brand: p.brand,
    trackingMode: p.tracking_mode || 'unit',
    usesPerUnit: p.uses_per_unit,
    useUnit: p.use_unit,
    totalUsesAvailable: p.total_uses_available,
    usesMinThreshold: p.uses_min_threshold,
    lastRestockTotalUses: p.last_restock_total_uses,
    costPerUse: p.cost_per_use,
  };
}

const categoryColors = { Hair: 'bg-blue-100 text-blue-700', Skin: 'bg-pink-100 text-pink-700', Nails: 'bg-purple-100 text-purple-700', Spa: 'bg-green-100 text-green-700', Other: 'bg-gray-100 text-gray-700' };

export default function Billing() {
  const [services, setServices] = useState([]);
  const [category, setCategory] = useState('All');
  const [searchTerm, setSearchTerm] = useState('');
  const [billItems, setBillItems] = useState([]);
  const [customerSearch, setCustomerSearch] = useState('');
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [discountAmt, setDiscountAmt] = useState(0);
  const [discountType, setDiscountType] = useState('flat');
  const [paymentMethod, setPaymentMethod] = useState('Cash');
  const [showSuccess, setShowSuccess] = useState(false);
  const [lastBill, setLastBill] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const [productsUsed, setProductsUsed] = useState([]);
  const [productPickerOpen, setProductPickerOpen] = useState(false);
  const [pickerSelections, setPickerSelections] = useState({});
  const [pickerSearch, setPickerSearch] = useState('');
  const [inventory, setInventory] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [selectedStaffId, setSelectedStaffId] = useState(null);
  const [selectedStaffName, setSelectedStaffName] = useState(null);
  const [recentBills, setRecentBills] = useState([]);
  const [showNewCustomerForm, setShowNewCustomerForm] = useState(false);
  const [newCustomerForm, setNewCustomerForm] = useState({ name: '', phone: '', email: '' });
  const [newCustomerErrors, setNewCustomerErrors] = useState({});
  const [newCustomerSaving, setNewCustomerSaving] = useState(false);
  const [newCustomerApiError, setNewCustomerApiError] = useState('');
  const user = JSON.parse(localStorage.getItem('salon_user') || '{}');
  const { t } = useLanguage();
  const { profile } = useSalonProfile();
  const location = useLocation();
  const preselectedCustomer = location.state?.customer ?? null;

  useEffect(() => {
    const load = async () => {
      try {
        const [svcRes, invRes, staffRes] = await Promise.all([
          api.get('/services'),
          api.get('/inventory').catch(() => ({ data: [] })),
          api.get('/staff').catch(() => ({ data: [] })),
        ]);
        setServices((svcRes.data || []).filter(s => s.active));
        setInventory(Array.isArray(invRes.data) ? invRes.data.map(apiProductToCamel) : []);
        const staff = Array.isArray(staffRes?.data) ? staffRes.data : [];
        setStaffList(staff.filter(s => s.is_active !== false));
      } catch {}
      setLoading(false);
    };
    load();
  }, []);

  useEffect(() => {
    if (preselectedCustomer && preselectedCustomer.id) {
      setSelectedCustomer(preselectedCustomer);
      setCustomerSearch(preselectedCustomer.name || '');
    }
  }, [preselectedCustomer]);

  useEffect(() => {
    if (showSuccess || loading) return;
    api.get('/bills', { params: { limit: 8 } })
      .then((r) => setRecentBills(Array.isArray(r.data) ? r.data : []))
      .catch(() => setRecentBills([]));
  }, [showSuccess, loading]);

  const searchCustomers = useCallback(async (q) => {
    if (q.length < 2) { setCustomers([]); return; }
    try {
      const r = await api.get(`/customers?search=${encodeURIComponent(q)}`);
      setCustomers(r.data);
    } catch {}
  }, []);

  const openNewCustomerForm = () => {
    setNewCustomerForm({ name: customerSearch.trim(), phone: '', email: '' });
    setNewCustomerErrors({});
    setNewCustomerApiError('');
    setShowNewCustomerForm(true);
  };

  const closeNewCustomerForm = () => {
    setShowNewCustomerForm(false);
    setNewCustomerErrors({});
    setNewCustomerApiError('');
  };

  const validateNewCustomerForm = () => {
    const err = {};
    const name = (newCustomerForm.name || '').trim();
    if (!name) err.name = t('val_name_required');
    else if (name.length < 2) err.name = t('val_name_min');
    const phone = (newCustomerForm.phone || '').replace(/\D/g, '');
    if (!phone || phone.length !== 10) err.phone = t('val_phone_10');
    else if (!/^\d+$/.test(phone)) err.phone = t('val_phone_numbers');
    const email = (newCustomerForm.email || '').trim();
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) err.email = t('val_email_invalid');
    setNewCustomerErrors(err);
    return Object.keys(err).length === 0;
  };

  const saveNewCustomer = async () => {
    if (!validateNewCustomerForm()) return;
    setNewCustomerApiError('');
    setNewCustomerSaving(true);
    try {
      const payload = {
        name: newCustomerForm.name.trim(),
        phone: newCustomerForm.phone.replace(/\D/g, ''),
        email: (newCustomerForm.email || '').trim() || '',
      };
      const r = await api.post('/customers', payload);
      const created = r.data;
      setSelectedCustomer({
        id: created.id,
        name: created.name,
        phone: created.phone,
        email: created.email || '',
        loyalty_points: created.loyalty_points ?? 0,
        loyalty_tier: created.loyalty_tier || 'Bronze',
      });
      toast.success(t('new_customer_created'));
      closeNewCustomerForm();
    } catch (err) {
      const msg = err.response?.data?.detail || err.response?.data?.message || err.message || t('bill_payment_failed');
      setNewCustomerApiError(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setNewCustomerSaving(false);
    }
  };

  const categories = ['All', ...new Set(services.map(s => s.category))];
  const filtered = services.filter(s => {
    const matchCat = category === 'All' || s.category === category;
    const matchSearch = s.name.toLowerCase().includes(searchTerm.toLowerCase());
    return matchCat && matchSearch;
  });

  const addItem = (svc) => {
    setBillItems(prev => {
      const idx = prev.findIndex(i => i.name === svc.name);
      if (idx >= 0) {
        const updated = [...prev];
        updated[idx] = { ...updated[idx], quantity: updated[idx].quantity + 1 };
        return updated;
      }
      return [...prev, { name: svc.name, price: svc.price, quantity: 1, gst_rate: svc.gst_rate, duration_mins: svc.duration_mins }];
    });
    toast.success(`${t('bill_added')} ${svc.name}`);
  };

  const updateQty = (idx, delta) => {
    setBillItems(prev => prev.map((item, i) => i === idx ? { ...item, quantity: Math.max(1, item.quantity + delta) } : item));
  };

  const removeItem = (idx) => {
    setBillItems(prev => prev.filter((_, i) => i !== idx));
  };

  const subtotal = billItems.reduce((s, i) => s + i.price * i.quantity, 0);
  const discountVal = discountType === 'percent' ? subtotal * (discountAmt / 100) : discountAmt;
  const afterDiscount = subtotal - discountVal;
  const totalGst = billItems.reduce((s, i) => {
    const it = i.price * i.quantity;
    const id = subtotal > 0 ? (it / subtotal) * discountVal : 0;
    return s + (it - id) * (i.gst_rate / 100);
  }, 0);
  const cgst = totalGst / 2;
  const sgst = totalGst / 2;
  const total = afterDiscount + totalGst;
  const ptsPerRupee = Number(profile?.loyalty_points_per_rupee) ?? 0;
  const loyaltyPts = ptsPerRupee > 0 ? Math.round(total * ptsPerRupee) : 0;
  const totalDuration = billItems.reduce((s, i) => s + (i.duration_mins || 0) * i.quantity, 0);
  const internalProductCost = productsUsed.reduce((s, pu) => {
    const prod = inventory.find(p => p.id === pu.productId);
    if (!prod) return s;
    if (isUseMode(prod)) return s + (Number(pu.quantity) || 0) * getCostPerUse(prod);
    return s + (Number(pu.quantity) || 0) * (Number(pu.costPrice) || 0);
  }, 0);

  const openProductPicker = () => {
    const init = {};
    productsUsed.forEach(pu => { init[pu.productId] = pu.quantity; });
    setPickerSelections(init);
    setPickerSearch('');
    setProductPickerOpen(true);
  };

  const confirmProductPicker = () => {
    const next = [];
    Object.entries(pickerSelections).forEach(([productId, qty]) => {
      const num = Math.max(0, Number(qty) || 0);
      if (num <= 0) return;
      const prod = inventory.find(p => p.id === productId);
      const avail = getAvailable(prod);
      if (!prod || avail < 1) return;
      const capped = Math.min(num, avail);
      const useMode = isUseMode(prod);
      next.push({
        productId,
        productName: prod.name,
        unit: useMode ? (prod.useUnit || 'stroke') : prod.unit,
        costPrice: prod.costPrice || 0,
        costPerUse: useMode ? getCostPerUse(prod) : undefined,
        quantity: capped,
        trackingMode: useMode ? 'use' : 'unit',
      });
    });
    setProductsUsed(next);
    setProductPickerOpen(false);
  };

  const setPickerQty = (productId, qtyOrDelta, isDelta = false) => {
    const prod = inventory.find(p => p.id === productId);
    const max = prod ? getAvailable(prod) : 0;
    setPickerSelections(prev => {
      const curr = prev[productId] || 0;
      const next = isDelta ? Math.max(0, Math.min(max, curr + qtyOrDelta)) : Math.max(0, Math.min(max, Number(qtyOrDelta) || 0));
      if (next === 0) {
        const { [productId]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [productId]: next };
    });
  };

  const updateProductQty = (productId, qty) => {
    const num = Math.max(0, Number(qty) || 0);
    const prod = inventory.find(p => p.id === productId);
    const max = prod ? getAvailable(prod) : 999;
    setProductsUsed(prev => prev.map(p => p.productId === productId ? { ...p, quantity: Math.min(Math.max(num, 0), max) } : p));
  };

  const removeProductUsed = (productId) => {
    setProductsUsed(prev => prev.filter(p => p.productId !== productId));
  };

  const handlePayment = async () => {
    if (billItems.length === 0) { toast.error(t('bill_add_items')); return; }
    setPaying(true);
    try {
      const res = await api.post('/bills', {
        customer_id: selectedCustomer?.id || '',
        customer_name: selectedCustomer?.name || 'Walk-in',
        items: billItems.map(({ name, price, quantity, gst_rate }) => ({ name, price, quantity, gst_rate })),
        discount_amount: discountAmt,
        discount_type: discountType,
        payment_method: paymentMethod,
        staff_id: selectedStaffId ?? '',
        staff_name: selectedStaffName ?? '',
      });
      const bill = res.data;
      const customerName = selectedCustomer?.name || 'Walk-in';
      const billId = bill.invoice_number || bill.id || `bill-${Date.now()}`;

      if (productsUsed.length > 0) {
        api.post('/bill-products', {
          bill_id: billId,
          products: productsUsed.map(pu => ({
            product_id: pu.productId,
            product_name: pu.productName,
            unit: pu.unit,
            cost_price: Number(pu.costPrice) || 0,
            quantity: Number(pu.quantity) || 0,
            tracking_mode: pu.trackingMode || 'unit',
            cost_per_use: pu.costPerUse ?? null,
          })),
        }).catch(() => {});

        const stockPromises = productsUsed.map(async (pu) => {
          const qty = Math.max(0, Number(pu.quantity) || 0);
          if (qty <= 0) return null;
          try {
            const adj = pu.trackingMode === 'use' ? qty : qty;
            const r = await api.patch(`/inventory/${pu.productId}/stock`, {
              adjustment: adj,
              type: 'reduce',
              reason: 'Used in Service',
            });
            return { product: apiProductToCamel(r.data), name: pu.productName };
          } catch {
            return null;
          }
        });
        const results = await Promise.all(stockPromises);
        results.forEach((r) => {
          if (!r) return;
          const p = r.product;
          const avail = p.tracking_mode === 'use' ? (p.total_uses_available ?? 0) : (p.stock ?? 0);
          const minTh = p.tracking_mode === 'use' ? (p.uses_min_threshold ?? 0) : (p.min_stock ?? 0);
          if (avail <= minTh && avail > 0) toast.warning(t('low_stock_toast').replace('{product}', r.name));
          if (avail === 0) toast.warning(t('low_stock_toast').replace('{product}', r.name));
        });
        const invRes = await api.get('/inventory').catch(() => ({ data: [] }));
        setInventory(Array.isArray(invRes.data) ? invRes.data.map(apiProductToCamel) : []);
      }

      api.patch('/salon-profile/invoice-number').catch(() => {});

      setLastBill(bill);
      setShowSuccess(true);
      confetti({ particleCount: 150, spread: 80, origin: { y: 0.6 } });
      toast.success(t('bill_complete'));
    } catch (err) {
      toast.error(t('bill_payment_failed'));
    } finally {
      setPaying(false);
    }
  };

  const generatePDF = (bill) => {
    const doc = new jsPDF();
    const salon = profile?.salon_name || profile?.salonName || user.salon_name || 'SalonSync';
    let y = 20;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(22);
    doc.text(salon, 105, y, { align: 'center' });
    y += 10;
    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    doc.text(`Invoice: ${bill.invoice_number}`, 105, y, { align: 'center' });
    y += 6;
    doc.text(`Date: ${new Date(bill.created_at).toLocaleString('en-IN')}`, 105, y, { align: 'center' });
    y += 6;
    doc.text(`Customer: ${bill.customer_name}`, 105, y, { align: 'center' });
    y += 12;
    doc.setLineWidth(0.5);
    doc.line(20, y, 190, y);
    y += 8;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text('Service', 22, y);
    doc.text('Qty', 110, y);
    doc.text('Price', 135, y);
    doc.text('Total', 170, y);
    y += 6;
    doc.setFont('helvetica', 'normal');

    bill.items.forEach(item => {
      doc.text(item.name, 22, y);
      doc.text(String(item.quantity), 114, y);
      doc.text(`${item.price.toFixed(0)}`, 135, y);
      doc.text(`${(item.price * item.quantity).toFixed(0)}`, 170, y);
      y += 6;
    });

    if (bill.staff_name) {
      y += 4;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(128, 128, 128);
      doc.text(`${t('served_by')}: ${bill.staff_name}`, 22, y);
      doc.setTextColor(0, 0, 0);
      y += 6;
    }

    y += 4;
    doc.line(20, y, 190, y);
    y += 8;
    doc.text(`Subtotal:`, 120, y); doc.text(`Rs. ${bill.subtotal.toFixed(2)}`, 170, y); y += 6;
    if (bill.discount_amount > 0) { doc.text(`Discount:`, 120, y); doc.text(`-Rs. ${bill.discount_amount.toFixed(2)}`, 170, y); y += 6; }
    doc.text(`CGST:`, 120, y); doc.text(`Rs. ${bill.cgst.toFixed(2)}`, 170, y); y += 6;
    doc.text(`SGST:`, 120, y); doc.text(`Rs. ${bill.sgst.toFixed(2)}`, 170, y); y += 8;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.text(`Total:`, 120, y); doc.text(`Rs. ${bill.total.toFixed(2)}`, 170, y); y += 10;
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`Payment: ${bill.payment_method}`, 105, y, { align: 'center' }); y += 5;
    if (bill.loyalty_points_earned > 0) {
      doc.text(`Loyalty Points Earned: ${bill.loyalty_points_earned}`, 105, y, { align: 'center' }); y += 10;
    }
    doc.text('Thank you for visiting! See you soon.', 105, y, { align: 'center' });

    doc.save(`Invoice-${bill.invoice_number}.pdf`);
    toast.success(t('bill_receipt_downloaded'));
  };

  const resetBill = () => {
    setBillItems([]);
    setSelectedCustomer(null);
    setSelectedStaffId(null);
    setSelectedStaffName(null);
    setDiscountAmt(0);
    setCustomerSearch('');
    setPaymentMethod('Cash');
    setShowSuccess(false);
    setLastBill(null);
    setProductsUsed([]);
    setPickerSelections({});
    setPickerSearch('');
    setShowNewCustomerForm(false);
    setNewCustomerApiError('');
  };

  if (showSuccess && lastBill) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Card className="max-w-md w-full border-0 shadow-xl rounded-3xl text-center animate-fade-in-up">
          <CardContent className="p-10">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-10 h-10 text-green-600" />
            </div>
            <h2 className="font-poppins text-2xl font-bold text-[#1a1a2e] mb-2">{t('bill_success')}</h2>
            <p className="text-gray-500 text-lg mb-2">{lastBill.customer_name}</p>
            <p className="font-poppins text-4xl font-bold text-[#1a1a2e] mb-1">₹{lastBill.total.toLocaleString('en-IN')}</p>
            {lastBill.loyalty_points_earned > 0 && (
              <Badge className="bg-[#f0a500]/10 text-[#f0a500] text-sm mb-2">+{lastBill.loyalty_points_earned} {t('bill_loyalty')}</Badge>
            )}
            {lastBill.staff_name && (
              <p className="text-sm text-gray-500 mb-6 flex items-center justify-center gap-1">
                <User className="w-4 h-4" /> {t('served_by')}: {lastBill.staff_name}
              </p>
            )}
            {!lastBill.staff_name && <div className="mb-6" />}
            <div className="space-y-3">
              <Button data-testid="download-receipt-btn" onClick={() => generatePDF(lastBill)} className="w-full h-14 rounded-xl bg-[#1a1a2e] hover:bg-[#2d2d44] text-white text-lg gap-2">
                <FileText className="w-5 h-5" /> {t('bill_download')}
              </Button>
              {(profile?.auto_send_bill_whatsapp !== false) && (
                <Button data-testid="send-whatsapp-btn" onClick={() => toast.info(t('bill_whatsapp_coming_soon'))} variant="outline" className="w-full h-14 rounded-xl text-lg gap-2 border-2 border-green-500 text-green-600 hover:bg-green-50">
                  <MessageCircle className="w-5 h-5" /> {t('bill_whatsapp')}
                </Button>
              )}
              <Button data-testid="new-bill-btn" onClick={resetBill} className="w-full h-14 rounded-xl bg-[#f0a500] hover:bg-[#d69400] text-[#1a1a2e] text-lg gap-2 font-semibold">
                <RotateCcw className="w-5 h-5" /> {t('bill_new')}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <div className="lg:col-span-3 space-y-4">{[1,2,3].map(i => <Skeleton key={i} className="h-32 rounded-2xl" />)}</div>
        <div className="lg:col-span-2"><Skeleton className="h-96 rounded-2xl" /></div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 items-start">
      {/* LEFT: Bill Builder */}
      <div className="lg:col-span-3 space-y-5">
        {/* Search */}
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <Input
            data-testid="service-search-input"
            placeholder={t('bill_search')}
            className="h-14 pl-12 text-lg rounded-xl"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
        </div>

        {/* Category Tabs */}
        <Tabs value={category} onValueChange={setCategory}>
          <TabsList className="bg-white rounded-xl p-1 h-auto flex flex-wrap gap-1">
            {categories.map(cat => (
              <TabsTrigger
                key={cat}
                value={cat}
                data-testid={`category-tab-${cat.toLowerCase()}`}
                className="rounded-lg px-4 py-2.5 text-base data-[state=active]:bg-[#1a1a2e] data-[state=active]:text-white"
              >
                {cat}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        {/* Service Tiles */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {filtered.map(svc => (
            <button
              key={svc.id}
              data-testid={`service-tile-${svc.id}`}
              onClick={() => addItem(svc)}
              className="bg-white rounded-2xl p-4 border border-gray-100 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 text-left active:scale-95 min-h-[100px]"
            >
              <p className="font-semibold text-[#1a1a2e] text-base mb-1 leading-tight">{svc.name}</p>
              <p className="text-[#f0a500] font-bold text-lg">₹{svc.price}</p>
              <div className="flex items-center gap-1 text-gray-400 text-sm mt-1">
                <Clock className="w-3.5 h-3.5" /> {svc.duration_mins}m
              </div>
            </button>
          ))}
        </div>

        {/* Bill Items */}
        {billItems.length > 0 && (
          <Card className="border-0 shadow-md rounded-2xl">
            <CardHeader className="pb-2">
              <CardTitle className="font-poppins text-lg">{t('bill_items')} ({billItems.length})</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {billItems.map((item, idx) => (
                <div key={idx} data-testid={`bill-item-${idx}`} className="flex items-center justify-between bg-[#faf8f5] rounded-xl px-4 py-3">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-[#1a1a2e] truncate">{item.name}</p>
                    <p className="text-sm text-gray-500">₹{item.price} x {item.quantity}</p>
                  </div>
                  <div className="flex items-center gap-2 ml-3">
                    <Button variant="outline" size="icon" className="w-9 h-9 rounded-lg" onClick={() => updateQty(idx, -1)} data-testid={`qty-minus-${idx}`}><Minus className="w-4 h-4" /></Button>
                    <span className="font-bold text-base w-6 text-center">{item.quantity}</span>
                    <Button variant="outline" size="icon" className="w-9 h-9 rounded-lg" onClick={() => updateQty(idx, 1)} data-testid={`qty-plus-${idx}`}><Plus className="w-4 h-4" /></Button>
                    <Button variant="ghost" size="icon" className="w-9 h-9 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg" onClick={() => removeItem(idx)} data-testid={`remove-item-${idx}`}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                  <p className="font-bold text-[#1a1a2e] w-20 text-right">₹{(item.price * item.quantity).toLocaleString('en-IN')}</p>
                </div>
              ))}
              {totalDuration > 0 && (
                <p className="text-sm text-gray-400 flex items-center gap-1 pt-1"><Clock className="w-3.5 h-3.5" /> {t('bill_estimated')}: {totalDuration}m</p>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {/* RIGHT: Customer + Payment */}
      <div className="lg:col-span-2 space-y-5">
        {/* Customer */}
        <Card className="border-0 shadow-md rounded-2xl">
          <CardHeader className="pb-3">
            <CardTitle className="font-poppins text-lg flex items-center gap-2"><User className="w-5 h-5" /> {t('bill_customer')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {selectedCustomer ? (
              <div className="bg-[#faf8f5] rounded-xl p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-[#1a1a2e] text-lg">{selectedCustomer.name}</p>
                    <p className="text-sm text-gray-500">{selectedCustomer.phone}</p>
                  </div>
                  <div className="text-right">
                    <Badge className="bg-[#f0a500]/10 text-[#f0a500]">{selectedCustomer.loyalty_tier}</Badge>
                    <p className="text-sm text-gray-500 mt-1 flex items-center gap-1 justify-end">
                      <Award className="w-3.5 h-3.5" /> {selectedCustomer.loyalty_points} pts
                    </p>
                  </div>
                </div>
                <Button variant="ghost" size="sm" className="mt-2 text-red-500 hover:text-red-700 px-0" onClick={() => setSelectedCustomer(null)}>
                  {t('bill_remove_cust')}
                </Button>
              </div>
            ) : showNewCustomerForm ? (
              <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-poppins font-semibold text-lg text-[#1a1a2e]">{t('new_customer_title')}</h3>
                  <button
                    type="button"
                    onClick={closeNewCustomerForm}
                    className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-[#1a1a2e] transition-colors"
                    aria-label={t('close')}
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <div className="space-y-3">
                  <div>
                    <label className="text-sm font-medium text-[#1a1a2e] block mb-1">{t('name')} *</label>
                    <Input
                      data-testid="new-customer-name"
                      className="h-12 rounded-xl"
                      value={newCustomerForm.name}
                      onChange={e => setNewCustomerForm(prev => ({ ...prev, name: e.target.value }))}
                      placeholder={t('name')}
                    />
                    {newCustomerErrors.name && <p className="text-red-500 text-sm mt-1">{newCustomerErrors.name}</p>}
                  </div>
                  <div>
                    <label className="text-sm font-medium text-[#1a1a2e] block mb-1">{t('phone')} *</label>
                    <Input
                      data-testid="new-customer-phone"
                      className="h-12 rounded-xl"
                      type="tel"
                      inputMode="numeric"
                      maxLength={10}
                      value={newCustomerForm.phone}
                      onChange={e => setNewCustomerForm(prev => ({ ...prev, phone: e.target.value.replace(/\D/g, '').slice(0, 10) }))}
                      placeholder={t('booking_new_customer_phone_placeholder')}
                    />
                    {newCustomerErrors.phone && <p className="text-red-500 text-sm mt-1">{newCustomerErrors.phone}</p>}
                  </div>
                  <div>
                    <label className="text-sm font-medium text-[#1a1a2e] block mb-1">{t('email')}</label>
                    <Input
                      data-testid="new-customer-email"
                      className="h-12 rounded-xl"
                      type="email"
                      value={newCustomerForm.email}
                      onChange={e => setNewCustomerForm(prev => ({ ...prev, email: e.target.value }))}
                      placeholder={t('email')}
                    />
                    {newCustomerErrors.email && <p className="text-red-500 text-sm mt-1">{newCustomerErrors.email}</p>}
                  </div>
                </div>
                {newCustomerApiError && <p className="text-red-500 text-sm">{newCustomerApiError}</p>}
                <Button
                  data-testid="new-customer-save-btn"
                  onClick={saveNewCustomer}
                  disabled={newCustomerSaving}
                  className="w-full h-12 rounded-xl bg-[#f0a500] hover:bg-[#d69400] text-[#1a1a2e] font-semibold"
                >
                  {newCustomerSaving ? t('saving') : t('new_customer_save')}
                </Button>
              </div>
            ) : (
              <>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    data-testid="customer-search-input"
                    placeholder={t('bill_search_cust')}
                    className="h-12 pl-10 rounded-xl"
                    value={customerSearch}
                    onChange={e => { setCustomerSearch(e.target.value); searchCustomers(e.target.value); }}
                  />
                </div>
                {customers.length > 0 && (
                  <div className="bg-white border border-gray-100 rounded-xl overflow-hidden max-h-48 overflow-y-auto">
                    {customers.map(c => (
                      <button
                        key={c.id}
                        data-testid={`customer-option-${c.id}`}
                        onClick={() => { setSelectedCustomer(c); setCustomerSearch(''); setCustomers([]); }}
                        className="w-full text-left px-4 py-3 hover:bg-[#faf8f5] border-b border-gray-50 last:border-0 transition-colors"
                      >
                        <span className="font-medium">{c.name}</span>
                        <span className="text-gray-400 ml-2">{c.phone}</span>
                      </button>
                    ))}
                  </div>
                )}
                {customerSearch.length >= 2 && customers.length === 0 && (
                  <div className="bg-[#faf8f5] border border-gray-100 rounded-xl p-4 space-y-3">
                    <p className="text-sm text-gray-600">{t('no_customer_found', { query: customerSearch })}</p>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={openNewCustomerForm}
                      className="w-full rounded-xl border-[#f0a500] text-[#f0a500] hover:bg-[#f0a500]/10 gap-1.5"
                      data-testid="billing-create-new-customer-btn"
                    >
                      <Plus className="w-4 h-4" /> {t('create_new_customer')}
                    </Button>
                  </div>
                )}
                <p className="text-sm text-gray-400 text-center">{t('bill_walkin')}</p>
              </>
            )}
          </CardContent>
        </Card>

        {/* Served By (Optional) */}
        <Card className="border-0 shadow-md rounded-2xl">
          <CardHeader className="pb-3">
            <CardTitle className="font-poppins text-lg flex items-center gap-2">
              <User className="w-5 h-5" /> {t('served_by')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <select
              data-testid="served-by-select"
              value={selectedStaffId ?? ''}
              onChange={(e) => {
                const val = e.target.value;
                if (val === '' || val === '__not_specified__') {
                  setSelectedStaffId(null);
                  setSelectedStaffName(null);
                  return;
                }
                const staff = staffList.find(s => s.id === val);
                if (staff) {
                  setSelectedStaffId(staff.id);
                  setSelectedStaffName(staff.name);
                }
              }}
              className="w-full h-12 rounded-xl border border-gray-200 bg-white px-4 text-base text-[#1a1a2e] focus:outline-none focus:ring-2 focus:ring-[#f0a500]/50 focus:border-[#f0a500]"
            >
              <option value="">{t('select_staff')}</option>
              {staffList.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name} — {s.role || 'Staff'}
                </option>
              ))}
              <option value="__not_specified__">{t('not_specified')}</option>
            </select>
            <p className="text-sm text-gray-400">{t('served_by_optional')}</p>
          </CardContent>
        </Card>

        {/* Products Used (Optional) */}
        <Card className="border-0 shadow-md rounded-2xl overflow-hidden">
          <div className="flex items-center justify-between px-5 py-4">
            <span className="font-poppins text-lg font-semibold flex items-center gap-2">
              <Package className="w-5 h-5" /> {t('bill_products_used')}
            </span>
            <Button variant="outline" size="sm" onClick={openProductPicker} className="rounded-lg border-[#f0a500] text-[#f0a500] hover:bg-[#f0a500]/10 gap-1">
              <Plus className="w-4 h-4" /> {t('add')}
            </Button>
          </div>
          <CardContent className="pt-0">
            {productsUsed.length > 0 ? (
              <div className="space-y-2">
                {productsUsed.map((pu) => {
                  const prod = inventory.find(p => p.id === pu.productId);
                  const maxQty = prod ? getAvailable(prod) : 999;
                  const cost = pu.trackingMode === 'use' && pu.costPerUse != null
                    ? (pu.quantity || 0) * pu.costPerUse
                    : (pu.quantity || 0) * (pu.costPrice || 0);
                  return (
                    <div key={pu.productId} className="flex items-center gap-2 bg-[#faf8f5] rounded-xl px-3 py-2">
                      <p className="flex-1 font-medium text-[#1a1a2e] truncate min-w-0">{pu.productName}</p>
                      <div className="flex items-center gap-1">
                        <Button variant="outline" size="icon" className="w-8 h-8 rounded-lg" onClick={() => updateProductQty(pu.productId, pu.quantity - 1)} disabled={pu.quantity <= 1}><Minus className="w-3.5 h-3.5" /></Button>
                        <Input type="number" min={1} max={maxQty} className="h-8 w-14 rounded-lg text-sm text-center" value={pu.quantity} onChange={e => updateProductQty(pu.productId, e.target.value)} />
                        <Button variant="outline" size="icon" className="w-8 h-8 rounded-lg" onClick={() => updateProductQty(pu.productId, pu.quantity + 1)} disabled={pu.quantity >= maxQty}><Plus className="w-3.5 h-3.5" /></Button>
                      </div>
                      <span className="text-sm text-gray-500 w-14">{pu.unit}</span>
                      <span className="text-sm font-medium w-16 text-right">₹{cost.toFixed(2)}</span>
                      <Button variant="ghost" size="icon" className="w-8 h-8 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg flex-shrink-0" onClick={() => removeProductUsed(pu.productId)} title={t('remove')}><X className="w-4 h-4" /></Button>
                    </div>
                  );
                })}
                <button type="button" onClick={openProductPicker} className="text-sm text-[#f0a500] hover:underline font-medium">
                  {t('bill_add_more')}
                </button>
              </div>
            ) : (
              <p className="text-sm text-gray-500 pb-2">0 {t('bill_products_added')}</p>
            )}
          </CardContent>
        </Card>

        {/* Pricing Summary */}
        <Card className="border-0 shadow-md rounded-2xl">
          <CardHeader className="pb-3">
            <CardTitle className="font-poppins text-lg">{t('bill_pricing')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center gap-2">
              <Input
                data-testid="discount-input"
                type="number"
                placeholder={t('bill_discount')}
                className="h-12 rounded-xl flex-1"
                value={discountAmt || ''}
                onChange={e => setDiscountAmt(Number(e.target.value))}
              />
              <div className="flex bg-gray-100 rounded-xl p-1">
                <button
                  data-testid="discount-flat-btn"
                  onClick={() => setDiscountType('flat')}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${discountType === 'flat' ? 'bg-white shadow text-[#1a1a2e]' : 'text-gray-500'}`}
                >₹</button>
                <button
                  data-testid="discount-percent-btn"
                  onClick={() => setDiscountType('percent')}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${discountType === 'percent' ? 'bg-white shadow text-[#1a1a2e]' : 'text-gray-500'}`}
                >%</button>
              </div>
            </div>

            <Separator />

            <div className="space-y-2 text-base">
              <div className="flex justify-between"><span className="text-gray-500">{t('bill_subtotal')}</span><span className="font-medium">₹{subtotal.toLocaleString('en-IN')}</span></div>
              {discountVal > 0 && <div className="flex justify-between text-green-600"><span>{t('bill_discount')}</span><span>-₹{discountVal.toFixed(0)}</span></div>}
              <div className="flex justify-between"><span className="text-gray-500">{t('bill_cgst')}</span><span>₹{cgst.toFixed(2)}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">{t('bill_sgst')}</span><span>₹{sgst.toFixed(2)}</span></div>
              <Separator />
              <div className="flex justify-between text-xl font-bold text-[#1a1a2e]">
                <span>{t('bill_total')}</span><span>₹{total.toFixed(2)}</span>
              </div>
              {internalProductCost > 0 && (
                <>
                  <Separator className="my-2 border-t border-gray-200" />
                  <div className="flex justify-between text-sm text-gray-500 pt-1">
                    <span>{t('bill_product_cost_internal')}</span><span>₹{internalProductCost.toFixed(2)}</span>
                  </div>
                </>
              )}
              {loyaltyPts > 0 && (
                <p className="text-sm text-[#f0a500] flex items-center gap-1"><Award className="w-4 h-4" /> +{loyaltyPts} {t('bill_loyalty')}</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Payment Method */}
        <Card className="border-0 shadow-md rounded-2xl">
          <CardHeader className="pb-3">
            <CardTitle className="font-poppins text-lg">{t('bill_pay_method')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-3">
              {[
                { value: 'Cash', icon: Banknote, label: t('bill_cash') },
                { value: 'Card', icon: CreditCard, label: t('bill_card') },
                { value: 'UPI', icon: Smartphone, label: t('bill_upi') },
                { value: 'Split', icon: ArrowLeftRight, label: t('bill_split') },
              ].map(m => (
                <button
                  key={m.value}
                  data-testid={`payment-${m.value.toLowerCase()}-btn`}
                  onClick={() => setPaymentMethod(m.value)}
                  className={`flex items-center gap-3 p-4 rounded-xl border-2 transition-all text-base font-medium ${
                    paymentMethod === m.value
                      ? 'border-[#f0a500] bg-[#f0a500]/5 text-[#1a1a2e]'
                      : 'border-gray-200 text-gray-500 hover:border-gray-300'
                  }`}
                >
                  <m.icon className="w-5 h-5" />{m.label}
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Pay Button */}
        <Button
          data-testid="mark-as-paid-btn"
          onClick={handlePayment}
          disabled={billItems.length === 0 || paying}
          className="w-full h-16 text-xl font-bold rounded-2xl bg-green-600 hover:bg-green-700 text-white shadow-lg shadow-green-200 transition-all active:scale-95 disabled:opacity-50"
        >
          <CheckCircle2 className="w-6 h-6 mr-2" />
          {paying ? t('bill_processing') : `${t('bill_mark_paid')} — ₹${total.toFixed(0)}`}
        </Button>

        {/* Recent Bills */}
        {recentBills.length > 0 && (
          <Card className="border-0 shadow-md rounded-2xl">
            <CardHeader className="pb-2">
              <CardTitle className="font-poppins text-base">{t('bill_recent_bills')}</CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 max-h-48 overflow-y-auto">
                {recentBills.slice(0, 6).map((b) => (
                  <li key={b.id} className="flex items-center justify-between gap-2 text-sm py-2 border-b border-gray-50 last:border-0">
                    <span className="truncate flex-1 min-w-0">
                      {(b.customer_name || 'Walk-in')} — {(b.items || []).map((i) => i.name).join(', ')}
                    </span>
                    {b.staff_name && (
                      <span className="text-gray-500 flex-shrink-0 ml-2">{b.staff_name}</span>
                    )}
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Product Picker Modal */}
      <Dialog open={productPickerOpen} onOpenChange={setProductPickerOpen}>
        <DialogContent className="bg-[#faf8f5] rounded-2xl sm:rounded-3xl max-w-[680px] w-[calc(100vw-2rem)] max-h-[90vh] flex flex-col p-4 sm:p-6">
          <DialogHeader>
            <DialogTitle className="font-poppins text-lg sm:text-xl">{t('bill_products_select')}</DialogTitle>
          </DialogHeader>
          <div className="relative mb-3 sm:mb-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder={t('bill_product_search_modal')}
              className="h-12 pl-10 rounded-xl"
              value={pickerSearch}
              onChange={e => setPickerSearch(e.target.value)}
            />
          </div>
          <div className="flex-1 overflow-y-auto min-h-0 space-y-4 pr-2">
            {(() => {
              const q = (pickerSearch || '').trim().toLowerCase();
              const filtered = q ? inventory.filter(p => p.name.toLowerCase().includes(q)) : inventory;
              const byCat = {};
              filtered.forEach(p => {
                const c = p.category || 'Other';
                if (!byCat[c]) byCat[c] = [];
                byCat[c].push(p);
              });
              const cats = ['Hair', 'Skin', 'Nails', 'Spa', 'Other'].filter(c => (byCat[c]?.length || 0) > 0);
              return cats.length === 0 ? (
                <p className="text-center text-gray-500 py-8">{t('inv_none')}</p>
              ) : (
                cats.map(cat => (
                  <div key={cat}>
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">{cat}</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {byCat[cat].map(p => {
                        const avail = getAvailable(p);
                        const isOut = isOutOfStock(p);
                        const isLow = isLowStock(p);
                        const useMode = isUseMode(p);
                        const qty = pickerSelections[p.id] || 0;
                        const selected = qty > 0;
                        const useUnitLabel = (p.useUnit || 'stroke').toLowerCase();
                        const useUnitPlural = useUnitLabel + (useUnitLabel.endsWith('s') ? '' : 's');
                        const derived = getDerivedUnitCount(p);
                        const availText = useMode
                          ? `${avail} ${useUnitPlural} ${t('bill_available_lower')} (${derived.toFixed(2)} ${p.unit})`
                          : `${p.stock} ${p.unit} ${t('bill_available_lower')}`;
                        const stepperLabel = useMode ? useUnitPlural : p.unit;
                        return (
                          <div
                            key={p.id}
                            className={`rounded-xl border-2 p-4 bg-white transition-all ${
                              isOut ? 'opacity-50 border-gray-200' : isLow ? 'border-amber-300 bg-amber-50/30' : selected ? 'border-[#f0a500] bg-[#f0a500]/5' : 'border-gray-100'
                            }`}
                          >
                            <div className="flex items-start justify-between mb-2">
                              <span className="font-semibold text-[#1a1a2e] text-sm flex items-center gap-1">
                                {isLow && <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0" />}
                                {p.name}
                              </span>
                              <Badge className={`${categoryColors[p.category] || categoryColors.Other} text-xs`}>{p.category}</Badge>
                            </div>
                            <p className="text-xs text-gray-500 mb-3">
                              {isOut ? t('bill_out_of_stock_label') : availText}
                            </p>
                            <div className="flex items-center gap-2">
                              <Button variant="outline" size="icon" className="w-8 h-8 rounded-lg" onClick={() => !isOut && setPickerQty(p.id, -1, true)} disabled={isOut || qty <= 0}><Minus className="w-3.5 h-3.5" /></Button>
                              <Input type="number" min={0} max={avail} className="h-8 w-14 rounded-lg text-sm text-center" value={qty} onChange={e => setPickerQty(p.id, e.target.value, false)} disabled={isOut} />
                              <Button variant="outline" size="icon" className="w-8 h-8 rounded-lg" onClick={() => !isOut && setPickerQty(p.id, 1, true)} disabled={isOut || qty >= avail}><Plus className="w-3.5 h-3.5" /></Button>
                              <span className="text-xs text-gray-500 ml-1">{stepperLabel}</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))
              );
            })()}
          </div>
          <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3 pt-4 border-t border-gray-200 mt-4 flex-shrink-0">
            <span className="text-sm text-gray-500 text-center sm:text-left order-2 sm:order-1">
              {Object.values(pickerSelections).reduce((s, v) => s + (v > 0 ? 1 : 0), 0)} {t('bill_products_selected')}
            </span>
            <div className="flex gap-2 justify-end sm:justify-start order-1 sm:order-2">
              <Button variant="outline" onClick={() => setProductPickerOpen(false)} className="rounded-xl flex-1 sm:flex-initial">{t('cancel')}</Button>
              <Button onClick={confirmProductPicker} disabled={Object.values(pickerSelections).filter(v => v > 0).length === 0} className="rounded-xl bg-[#f0a500] hover:bg-[#d69400] text-[#1a1a2e] font-semibold flex-1 sm:flex-initial">
                {t('bill_confirm_add')} ✓
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
